import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users } from "lucide-react";
import { BLOG_POSTS } from "../data/mock";
import { useRevealGroup } from "../hooks/useReveal";
import { PageHero, CTASection } from "../components/Shared";

const CATEGORIES = ["All", "Super Visa", "Visitor Insurance", "Life Insurance", "Critical Illness", "Disability"];
const PAGE_DISCLAIMER = "Abhi Insurance Agency Inc. is a licensed insurance advisory based in Surrey, British Columbia. The information on this page is general in nature and is not financial, legal or insurance advice. Coverage terms, eligibility, premiums and program requirements vary by insurer and by individual circumstances, and change over time. Nothing here guarantees eligibility, approval or a particular premium. Speak with a licensed advisor before making any insurance decision, and refer to your policy documents for the terms that actually apply to you.";

const MOBILE_PAGE_DISCLAIMER =
  "This information is general only and is not financial, legal or insurance advice. Coverage, eligibility and premiums vary. Speak with a licensed advisor before making an insurance decision.";

function useBlogIndexMetadata() {
  useEffect(() => {
    const previousTitle = document.title;
    const existing = document.head.querySelector('meta[name="description"]');
    const previousDescription = existing?.getAttribute("content") ?? null;
    const meta = existing || document.createElement("meta");
    if (!existing) {
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    document.title = "Insurance Guides and Advice | Abhi Insurance Agency Surrey BC";
    meta.setAttribute("content", "Plain language guides to Super Visa, life, critical illness and disability insurance in Canada, written by a licensed advisor in Surrey BC.");
    return () => {
      document.title = previousTitle;
      if (!existing) meta.remove();
      else if (previousDescription === null) meta.removeAttribute("content");
      else meta.setAttribute("content", previousDescription);
    };
  }, []);
}

// Remount the reveal group when filters change to observe the visible cards.
function BlogResults({ posts }) {
  const ref = useRevealGroup();
  return (
    <div ref={ref} id="blog-results" aria-live="polite" className="mt-12 grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, index) => (
        <Link
          key={post.slug}
          to={"/blog/" + post.slug}
          data-reveal
          style={{ transitionDelay: index * 80 + "ms" }}
          data-testid={"blog-list-card-" + post.slug}
          className="group flex flex-col overflow-hidden rounded-2xl bg-white shadow-[0_2px_16px_rgba(0,111,185,0.07)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[0_24px_48px_rgba(0,111,185,0.15)]"
        >
          <div className="relative h-52 overflow-hidden">
            <img src={post.image} alt={post.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
            <span className="absolute left-4 top-4 rounded-full bg-white/95 px-3.5 py-1 text-xs font-bold text-[#006FB9]">{post.category}</span>
          </div>
          <div className="flex flex-1 flex-col p-7">
            <p className="text-xs font-medium text-[#70879A]">{post.date} · {post.readTime}</p>
            <h3 className="mt-2.5 font-heading text-lg font-bold leading-snug text-[#0B2748] transition-colors duration-300 group-hover:text-[#0785C7]">{post.title}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-[#526A7D]">{post.excerpt}</p>
           <div className="mt-5 flex items-center gap-3 border-t border-[#E1ECF3] pt-4">
  <div
    className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#E2F2FA] text-[#006FB9] ring-2 ring-[#E2F2FA]"
    aria-hidden="true"
  >
    <Users className="h-5 w-5" />
  </div>

  <p className="text-sm font-semibold text-[#193B58]">
    Team Abhi Insurance
  </p>
</div>
            <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-[#006FB9]">
              Read Article <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </span>
          </div>
        </Link>
      ))}
    </div>
  );
}

export default function Blog() {
  const ref = useRevealGroup();
  const [filter, setFilter] = useState("All");
  const posts = filter === "All" ? BLOG_POSTS : BLOG_POSTS.filter((post) => post.category === filter);
  useBlogIndexMetadata();

  return (
    <div ref={ref}>
      <PageHero
        badge="Our Blog"
        title="Insurance Insights, Made Simple"
        subtitle="Practical guides to Super Visa, visitor, life, critical illness and disability coverage in Canada, written by a licensed advisor who places these policies every week."
        image="https://images.pexels.com/photos/3768126/pexels-photo-3768126.jpeg?auto=compress&cs=tinysrgb&w=2000"
        imageAlt="Professionals sharing insurance knowledge and insights"
      />
      <section className="bg-[#F4F7FB] py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex flex-wrap items-center justify-center gap-3" data-reveal>
            {CATEGORIES.map((category) => (
              <button
                type="button"
                key={category}
                onClick={() => setFilter(category)}
                aria-pressed={filter === category}
                aria-controls="blog-results"
                data-testid={"blog-filter-" + category.toLowerCase().replace(/\s/g, "-")}
                className={"rounded-full px-5 py-2 text-sm font-semibold transition-all duration-300 " + (
                  filter === category ? "bg-[#006FB9] text-white shadow-md" : "bg-white text-[#294A65] hover:bg-[#E2F2FA]"
                )}
              >
                {category}
              </button>
            ))}
          </div>
          <BlogResults key={filter} posts={posts} />
        </div>
      </section>
      <CTASection
        title="Questions About Your Coverage?"
        subtitle={<>Free consultations. <a href="tel:+16049966862" className="hover:underline">Call (604) 996 6862.</a></>}
      />
    <section
  aria-label="Service and page disclaimer"
  className="bg-[#F4F7FB] py-8 lg:py-10"
>
  <div className="mx-auto max-w-7xl px-6">
    <div className="rounded-xl border-l-[5px] border-r-[5px] border-[#0B4FAF] bg-white px-5 py-5 shadow-[0_8px_24px_rgba(11,39,72,0.08)] sm:px-7 lg:px-8">
      
      {/* Short disclaimer for mobile */}
      <p className="text-sm leading-relaxed text-[#526A7D] md:hidden">
        {MOBILE_PAGE_DISCLAIMER}
      </p>

      {/* Complete disclaimer for tablet and desktop */}
      <p className="hidden text-sm leading-relaxed text-[#526A7D] md:block">
        {PAGE_DISCLAIMER}
      </p>
      
    </div>
  </div>
</section>
    </div>
  );
}

